/**
 * Complement of an Event generator (6.DPSR.2.3).
 *
 * P(not event) = 1 - P(event). The event probability is chosen first (as an
 * exact Rational with an SC-friendly denominator) and the complement is
 * derived, so the stated value and the graded answer always agree. The answer
 * is a reduced fraction; the grader accepts an equivalent fraction or decimal.
 *
 * Families:
 *   F1: event probability given as a fraction
 *   F2: event probability given as a decimal
 *   F3: event probability given inside a word context
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "complement-probability" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

/** SC-friendly denominators. */
const DENOMS: readonly number[] = [2, 4, 5, 10];

const DIFF: Record<Difficulty, readonly Family[]> = {
  easy: ["F1"],
  standard: ["F1", "F2"],
  hard: ["F1", "F2", "F3"],
  challenge: ["F2", "F3"],
};

const ONE = rat(1);

/** Word contexts for F3, each with its own phrasing of "the event". */
const CONTEXTS: readonly { event: string; complement: string }[] = [
  { event: "it rains today", complement: "it does not rain today" },
  { event: "the bus is on time", complement: "the bus is not on time" },
  { event: "you win the game", complement: "you do not win the game" },
  { event: "the light is green", complement: "the light is not green" },
];

export class ComplementProbabilityGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const family = rng.pick(DIFF[ctx.difficulty]);

      let p: Rational;
      let valueText: string;
      let promptText: string;

      if (family === "F2") {
        // Decimal from tenths or hundredths, strictly between 0 and 1.
        const useHundredths = rng.bool();
        const denom = useHundredths ? 100 : 10;
        const n = rng.int(1, denom - 1);
        p = rat(n, denom);
        valueText = p.toNumber().toFixed(useHundredths ? 2 : 1);
        promptText = `The probability that an event happens is ${valueText}. Find the probability that it does not happen.`;
      } else if (family === "F3") {
        const d = rng.pick(DENOMS);
        const n = rng.int(1, d - 1);
        p = rat(n, d);
        valueText = `${n}/${d}`;
        const c = rng.pick(CONTEXTS);
        promptText = `The probability that ${c.event} is ${valueText}. Find the probability that ${c.complement}.`;
      } else {
        // F1: plain fraction.
        const d = rng.pick(DENOMS);
        const n = rng.int(1, d - 1);
        p = rat(n, d);
        valueText = `${n}/${d}`;
        promptText = `P(event) = ${valueText}. Find P(not event).`;
      }

      // p is strictly between 0 and 1 by construction; complement is too.
      const answer: Rational = ONE.sub(p);

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: promptText },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [
          {
            description:
              "The complement is 1 minus the probability of the event.",
            latex: `1 - ${p.toLatex()} = ${answer.toLatex()}`,
          },
        ],
        hints: [
          { level: 1, text: "An event and its complement together have probability 1." },
          { level: 2, text: "Subtract the event's probability from 1." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const complementProbability = new ComplementProbabilityGenerator();
